import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../store';
import { downloadTaskAttachment } from '../../store/actions/TasksActions';
import { PiCloudArrowDownLight } from 'react-icons/pi';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import Button from '../shared/TailwindComponents/Button';
import { sanitizeHTML } from '../../utils/security/htmlSanitizer';
import SafePdfViewer from '../shared/SafePdfViewer';
import { useTranslation } from 'react-i18next';

interface AttachmentViewerProps {
  taskUuid?: string;
  attachment: {
    AttachmentID?: string;
    id?: string;
    FileName?: string;
    FileType?: string;
    ContentType?: string;
    size?: number;
  };
  blob: Blob;
  contentType: string;
  filename: string;
  onDownload?: () => Promise<void>;
}

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  taskUuid,
  attachment,
  blob,
  contentType,
  filename,
  onDownload
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [viewerData, setViewerData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadViewer();
  }, [blob, contentType]);

  const loadViewer = async () => {
    setLoading(true);
    setError(null);

    try {

      // Validate blob before processing
      if (!blob || blob.size === 0) {
        console.warn('[AttachmentViewer] Invalid or empty blob received');
        setError(t('File is empty or invalid'));
        setViewerData({ type: 'download', filename });
        setLoading(false);
        return;
      }

      const blobUrl = window.URL.createObjectURL(blob);

      // Normalize content type for matching (remove charset, etc.)
      const normalizedContentType = contentType.toLowerCase().split(';')[0].trim();

      // PDFs - display in iframe
      if (normalizedContentType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) {
        
        setViewerData({
          type: 'pdf',
          src: blobUrl,
          style: { width: '100%', height: '100%' }
        });
        return;
      }

      // Images - display as img
      if (normalizedContentType.startsWith('image/')) {
        setViewerData({
          type: 'image',
          src: blobUrl,
          style: { maxWidth: '100%', maxHeight: '600px' }
        });
        return;
      }

      // Text files - read as text
      if (
        normalizedContentType.startsWith('text/') ||
        normalizedContentType === 'application/json' ||
        normalizedContentType === 'application/xml' ||
        filename.toLowerCase().endsWith('.txt') ||
        filename.toLowerCase().endsWith('.csv') ||
        filename.toLowerCase().endsWith('.json') ||
        filename.toLowerCase().endsWith('.xml')
      ) {
        try {
          const text = await blob.text();
          const language = getLanguageFromType(normalizedContentType);
          setViewerData({
            type: 'text',
            content: text,
            language
          });
          return;
        } catch (err) {
          console.error('[AttachmentViewer] Failed to read text content:', err);
          setError(t('Could not read file content'));
          setViewerData({ type: 'download', filename });
          return;
        }
      }

      // Word documents (.docx) - convert to HTML using native handler
      if (
        normalizedContentType.includes('vnd.openxmlformats-officedocument.wordprocessingml') ||
        filename.toLowerCase().endsWith('.docx') ||
        filename.toLowerCase().endsWith('.doc')
      ) {
        try {
          const { extractHtmlFromDocx } = await import('../../utils/docxHandler');
          const html = await extractHtmlFromDocx(await blob.arrayBuffer());
          const sanitizedHtml = sanitizeHTML(html);
          setViewerData({
            type: 'docx',
            html: sanitizedHtml
          });
          return;
        } catch (err) {
          console.error('[AttachmentViewer] Failed to convert Word document:', err);
          setError(t('Could not render Word document'));
          setViewerData({
            type: 'download',
            filename
          });
          return;
        }
      }

      // Excel files (.xlsx, .xls) - convert to HTML table using xlsx
      if (
        normalizedContentType.includes('vnd.openxmlformats-officedocument.spreadsheetml') ||
        normalizedContentType.includes('application/vnd.ms-excel') ||
        filename.toLowerCase().endsWith('.xlsx') ||
        filename.toLowerCase().endsWith('.xls')
      ) {
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });

          // Convert all sheets to HTML tables
          const htmlParts: string[] = [];
          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            const html = XLSX.utils.sheet_to_html(worksheet);
            // Sanitize both the sheet name and the HTML content
            const sanitizedSheetName = sanitizeHTML(sheetName);
            const sanitizedHtml = sanitizeHTML(html);
            htmlParts.push(`<div class="mb-8"><h3 class="text-lg font-semibold mb-3">${sanitizedSheetName}</h3>${sanitizedHtml}</div>`);
          });

          setViewerData({
            type: 'xlsx',
            html: htmlParts.join('')
          });
          return;
        } catch (err) {
          console.error('[AttachmentViewer] Failed to convert Excel file:', err);
          setError(t('Could not render Excel file'));
          setViewerData({
            type: 'download',
            filename
          });
          return;
        }
      }

      // PowerPoint, etc - offer download
      if (
        normalizedContentType.includes('vnd.openxmlformats-officedocument.presentationml') ||
        filename.toLowerCase().endsWith('.pptx') ||
        filename.toLowerCase().endsWith('.ppt')
      ) {
        setViewerData({
          type: 'download',
          filename
        });
        return;
      }

      // Fallback - show download button
      setViewerData({
        type: 'download',
        filename
      });
    } catch (err) {
      setError(t('Error loading attachment viewer'));
      setViewerData({
        type: 'download',
        filename
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (onDownload) {
      try {
        await onDownload();
      } catch (err) {
        setError(t('Download failed'));
      }
      return;
    }

    const attachmentId = attachment.AttachmentID || attachment.id;
    if (!attachmentId || !taskUuid) {
      console.error('[AttachmentViewer] Cannot download - missing attachment ID or taskUuid');
      setError(t('Missing attachment ID'));
      return;
    }
    await dispatch(downloadTaskAttachment(taskUuid, attachmentId, filename) as any);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('Loading attachment...')}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">({filename})</p>
        </div>
      </div>
    );
  }

  if (error && viewerData?.type !== 'download') {
    console.error('[AttachmentViewer] Displaying error state:', error);
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-4 w-full max-w-md">
          <p className="text-red-700 dark:text-red-300 font-medium mb-2">{t('Unable to Display File')}</p>
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <p className="text-xs text-red-500 dark:text-red-400 mt-2 font-mono break-all">{filename}</p>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <PiCloudArrowDownLight className="w-4 h-4" />
          {t('Download instead')}
        </button>
      </div>
    );
  }

  if (!viewerData) {
    console.warn('[AttachmentViewer] No viewer data - showing download');
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-gray-600 dark:text-gray-400 mb-4">{t('Unable to display this file type')}</p>
        <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">Content-Type: {contentType}</p>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <PiCloudArrowDownLight className="w-4 h-4" />
          {t('Download')} {filename}
        </button>
      </div>
    );
  }


  return (
    <div className="attachment-viewer flex flex-col h-full w-full">
      {/* Content viewer - takes up remaining space */}
      <div className="attachment-content flex-1 w-full bg-white dark:bg-gray-900 overflow-auto custom-scrollbar-main">
        {viewerData.type === 'pdf' && (
          <SafePdfViewer pdfUrl={viewerData.src} filename={filename} />
        )}

        {viewerData.type === 'image' && (
          <div className="flex items-center justify-center h-full p-4 bg-white dark:bg-gray-900">
            <img
              src={viewerData.src}
              alt={filename}
              style={viewerData.style}
              className="object-contain"
            />
          </div>
        )}

        {viewerData.type === 'text' && (
          <div className="bg-gray-100 dark:bg-gray-900 h-full overflow-auto">
            <div className="max-w-4xl mx-auto p-6">
              <pre className="text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                <code>{viewerData.content}</code>
              </pre>
            </div>
          </div>
        )}

        {viewerData.type === 'docx' && (
          <div className="bg-white dark:bg-gray-900 h-full overflow-auto custom-scrollbar-main">
            <div className="max-w-4xl mx-auto p-6">
              <div
                className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100"
                dangerouslySetInnerHTML={{ __html: viewerData.html }}
              />
            </div>
          </div>
        )}

        {viewerData.type === 'xlsx' && (
          <div className="bg-white dark:bg-gray-900 h-full overflow-auto">
            <div className="max-w-5xl mx-auto p-6">
              <div
                className="max-w-none text-gray-900 dark:text-gray-100"
                dangerouslySetInnerHTML={{ __html: viewerData.html }}
                style={{
                  fontSize: '0.875rem'
                }}
              />
            <style>{`
              table {
                border-collapse: collapse;
                margin-bottom: 1.5rem;
                border: 1px solid #d1d5db;
              }
              th {
                background-color: #f3f4f6;
                border: 1px solid #d1d5db;
                padding: 0.5rem;
                font-weight: 600;
              }
              td {
                border: 1px solid #d1d5db;
                padding: 0.5rem;
              }
              .dark & th {
                background-color: #374151;
              }
              .dark & {
                border-color: #4b5563;
              }
            `}</style>
            </div>
          </div>
        )}

        {viewerData.type === 'download' && (
          <div className="flex flex-col items-center justify-center h-full text-center bg-gray-50 dark:bg-gray-800">
            <PiCloudArrowDownLight className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('This file type cannot be displayed in the browser')}
            </p>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <PiCloudArrowDownLight className="w-4 h-4" />
              {t('Download')} {filename}
            </button>
          </div>
        )}
      </div>

      {/* Download toolbar at bottom */}
      {viewerData.type !== 'download' && (
        <div className="flex justify-end px-4 pt-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <Button
            onClick={handleDownload}
            size='small'
          >
            <PiCloudArrowDownLight className="w-4 h-4 mr-1" />
            {t('Download')}
          </Button>
        </div>
      )}
    </div>
  );
};

function getLanguageFromType(contentType: string): string {
  if (contentType.includes('javascript')) return 'javascript';
  if (contentType.includes('xml')) return 'xml';
  if (contentType.includes('json')) return 'json';
  if (contentType.includes('html')) return 'html';
  if (contentType.includes('markdown') || contentType.includes('plain')) return 'markdown';
  return 'text';
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default AttachmentViewer;
